import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  ORG_ROLE_HIERARCHY,
  OrgRole,
  inviteMemberSchema,
  roleAtLeast,
  type InvitationSummary,
  type InviteMemberInput,
  type InviteResult,
  type Member,
} from '@ddp/shared';
import Button from '@carbon/react/es/components/Button';
import Form from '@carbon/react/es/components/Form/Form';
import TextInput from '@carbon/react/es/components/TextInput';
import Select from '@carbon/react/es/components/Select/Select';
import SelectItem from '@carbon/react/es/components/SelectItem';
import Tag from '@carbon/react/es/components/Tag/Tag';
import Loading from '@carbon/react/es/components/Loading/Loading';
import Modal from '@carbon/react/es/components/Modal/Modal';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import TrashCan from '@carbon/icons-react/es/TrashCan';
import Copy from '@carbon/icons-react/es/Copy';

import { apiFetch } from '../api/client.js';
import { useAuthStore } from '../auth/store.js';
import { zodResolver } from '../lib/zod-resolver.js';
import { PageHeader } from '../components/PageHeader.js';
import { HelpHint } from '../components/HelpHint.js';

/** Libellés de rôle côté utilisateur (identifiants en anglais, affichage en français). */
const ROLE_LABEL: Record<OrgRole, string> = {
  VIEWER: 'Lecteur',
  ANNOTATOR: 'Annotateur',
  EDITOR: 'Éditeur',
  ADMIN: 'Administrateur',
  OWNER: 'Propriétaire',
};

/**
 * Administration des membres (volet A). Liste des membres avec leur rôle, changement de rôle et
 * retrait (réservés aux administrateurs), formulaire d'invitation et invitations en attente.
 *
 * Les garde-fous sont **appliqués côté serveur** (dernier propriétaire, gestion du rôle OWNER
 * réservée à un OWNER, pas d'auto-modification) ; l'UI ne fait que les refléter pour éviter des
 * actions vouées à échouer, et affiche tel quel le message d'erreur du serveur le cas échéant.
 */
export function MembersPage() {
  const currentUser = useAuthStore((state) => state.currentUser);
  const myRole = currentUser?.memberships[0]?.role ?? OrgRole.VIEWER;
  const isAdmin = roleAtLeast(myRole, OrgRole.ADMIN);
  const isOwner = myRole === OrgRole.OWNER;

  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<InvitationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<Member | null>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const m = await apiFetch<Member[]>('/organizations/current/members');
        // Les invitations en attente ne sont visibles qu'aux administrateurs (le back renvoie 404
        // sinon) : on ne tente l'appel que si le rôle le permet.
        const inv = isAdmin
          ? await apiFetch<InvitationSummary[]>('/organizations/current/invitations')
          : [];
        if (!active) return;
        setMembers(m);
        setInvitations(inv);
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Chargement impossible.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [isAdmin]);

  const changeRole = async (member: Member, role: OrgRole) => {
    setError(null);
    setNotice(null);
    try {
      const updated = await apiFetch<Member>(`/organizations/current/members/${member.userId}`, {
        method: 'PATCH',
        body: { role },
      });
      setMembers((list) => list.map((x) => (x.userId === updated.userId ? updated : x)));
      setNotice(`Rôle de ${updated.displayName} mis à jour : ${ROLE_LABEL[updated.role]}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Changement de rôle impossible.');
    }
  };

  const confirmRemove = async () => {
    if (!pendingRemove) return;
    const member = pendingRemove;
    setPendingRemove(null);
    setError(null);
    setNotice(null);
    try {
      await apiFetch(`/organizations/current/members/${member.userId}`, { method: 'DELETE' });
      setMembers((list) => list.filter((x) => x.userId !== member.userId));
      setNotice(`${member.displayName} a été retiré de l’organisation.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Retrait impossible.');
    }
  };

  const revokeInvitation = async (invitation: InvitationSummary) => {
    setError(null);
    setNotice(null);
    try {
      await apiFetch(`/organizations/current/invitations/${invitation.id}`, { method: 'DELETE' });
      setInvitations((list) => list.filter((x) => x.id !== invitation.id));
      setNotice(`Invitation de ${invitation.email} révoquée.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Révocation impossible.');
    }
  };

  // Un envoi peut retourner un membre ajouté directement (compte existant) ou une invitation
  // pendante : les deux mettent à jour la liste concernée.
  const onInvited = (result: InviteResult, email: string) => {
    if (result.status === 'added' && result.member) {
      setMembers((list) => [...list, result.member as Member]);
      setNotice(`${email} a un compte : ajouté·e directement à l’organisation.`);
    } else {
      void refreshInvitations();
    }
  };

  const refreshInvitations = async () => {
    try {
      const inv = await apiFetch<InvitationSummary[]>('/organizations/current/invitations');
      setInvitations(inv);
    } catch {
      // Sans conséquence : la liste se rechargera à la prochaine visite.
    }
  };

  if (loading) {
    return (
      <section className="ddp-page__section">
        <Loading withOverlay={false} description="Chargement…" />
      </section>
    );
  }

  return (
    <section className="ddp-page__section ddp-members">
      <PageHeader
        title="Membres"
        lead="Les personnes de votre organisation, leurs rôles et les invitations en attente."
      />

      {error && (
        <InlineNotification
          kind="error"
          title="Erreur"
          subtitle={error}
          lowContrast
          onClose={() => setError(null)}
        />
      )}
      {notice && (
        <InlineNotification
          kind="success"
          title={notice}
          lowContrast
          onClose={() => setNotice(null)}
        />
      )}

      {isAdmin && <InviteForm isOwner={isOwner} onResult={onInvited} onError={setError} />}

      <h2 className="ddp-members__subtitle">
        Membres <span className="ddp-members__count">{members.length}</span>
        <HelpHint textKey="help.members.roles" />
      </h2>
      <ul className="ddp-members__list">
        {members.map((member) => {
          const isSelf = member.userId === currentUser?.id;
          return (
            <li key={member.userId} className="ddp-member">
              <div className="ddp-member__id">
                <span className="ddp-member__name">
                  {member.displayName}
                  {isSelf && <span className="ddp-member__you"> (vous)</span>}
                </span>
                <span className="ddp-member__email">{member.email}</span>
              </div>
              {isAdmin && !isSelf ? (
                <Select
                  id={`role-${member.userId}`}
                  className="ddp-member__role"
                  labelText=""
                  hideLabel
                  value={member.role}
                  onChange={(e) => void changeRole(member, e.target.value as OrgRole)}
                >
                  {ORG_ROLE_HIERARCHY.filter(
                    // Le rôle OWNER n'est proposé qu'à un propriétaire (le serveur refuserait sinon) ;
                    // il reste affiché si le membre l'a déjà, pour ne pas masquer son rôle courant.
                    (role) => role !== OrgRole.OWNER || isOwner || member.role === OrgRole.OWNER,
                  ).map((role) => (
                    <SelectItem key={role} value={role} text={ROLE_LABEL[role]} />
                  ))}
                </Select>
              ) : (
                <Tag type={member.role === OrgRole.OWNER ? 'purple' : 'blue'} size="sm">
                  {ROLE_LABEL[member.role]}
                </Tag>
              )}
              {isAdmin && !isSelf && (
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={TrashCan}
                  iconDescription="Retirer de l’organisation"
                  tooltipPosition="left"
                  onClick={() => setPendingRemove(member)}
                />
              )}
            </li>
          );
        })}
      </ul>

      {isAdmin && invitations.length > 0 && (
        <>
          <h2 className="ddp-members__subtitle">
            Invitations en attente <span className="ddp-members__count">{invitations.length}</span>
          </h2>
          <ul className="ddp-members__list">
            {invitations.map((invitation) => (
              <li key={invitation.id} className="ddp-member ddp-member--pending">
                <div className="ddp-member__id">
                  <span className="ddp-member__name">{invitation.email}</span>
                  <span className="ddp-member__email">
                    {ROLE_LABEL[invitation.role]}
                    {invitation.invitedByName && ` · invité·e par ${invitation.invitedByName}`} ·
                    expire le {formatDate(invitation.expiresAt)}
                  </span>
                </div>
                <Tag type="outline" size="sm">
                  En attente
                </Tag>
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={TrashCan}
                  iconDescription="Révoquer l’invitation"
                  tooltipPosition="left"
                  onClick={() => void revokeInvitation(invitation)}
                />
              </li>
            ))}
          </ul>
        </>
      )}

      <Modal
        open={pendingRemove !== null}
        danger
        modalHeading="Retirer ce membre ?"
        primaryButtonText="Retirer"
        secondaryButtonText="Annuler"
        onRequestClose={() => setPendingRemove(null)}
        onRequestSubmit={() => void confirmRemove()}
      >
        <p>
          {pendingRemove?.displayName} perdra l’accès à cette organisation. Son compte et ses
          données personnelles ne sont pas supprimés ; il pourra être réinvité plus tard.
        </p>
      </Modal>
    </section>
  );
}

/**
 * Formulaire d'invitation. À la validation, deux cas :
 * - l'e-mail n'a pas de compte → un **lien à usage unique** est affiché (à transmettre à la
 *   personne, pas d'e-mail automatique à ce stade) ;
 * - l'e-mail a déjà un compte → la personne est ajoutée directement (remontée au parent).
 */
function InviteForm({
  isOwner,
  onResult,
  onError,
}: {
  isOwner: boolean;
  onResult: (result: InviteResult, email: string) => void;
  onError: (message: string) => void;
}) {
  const [link, setLink] = useState<{ url: string; email: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const assignableRoles = useMemo(
    () => ORG_ROLE_HIERARCHY.filter((role) => role !== OrgRole.OWNER || isOwner),
    [isOwner],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<InviteMemberInput>({
    resolver: zodResolver(inviteMemberSchema),
    defaultValues: { role: OrgRole.VIEWER },
  });

  const onSubmit = handleSubmit(async (values) => {
    setLink(null);
    setCopied(false);
    try {
      const result = await apiFetch<InviteResult>('/organizations/current/invitations', {
        method: 'POST',
        body: values,
      });
      onResult(result, values.email);
      if (result.status === 'invited' && result.inviteUrl) {
        setLink({ url: result.inviteUrl, email: values.email });
      }
      reset({ email: '', role: values.role });
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Invitation impossible.');
    }
  });

  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
    } catch {
      // Le presse-papiers peut être refusé (contexte non sécurisé) : le champ reste sélectionnable.
    }
  };

  return (
    <section className="ddp-members__invite">
      <h2 className="ddp-members__subtitle">
        Inviter une personne
        <HelpHint textKey="help.members.invite" />
      </h2>
      <Form onSubmit={onSubmit}>
        <div className="ddp-members__invite-row">
          <TextInput
            id="invite-email"
            type="email"
            labelText="Adresse e-mail"
            placeholder="personne@exemple.io"
            invalid={Boolean(errors.email)}
            invalidText={errors.email?.message}
            {...register('email')}
          />
          <Select
            id="invite-role"
            labelText="Rôle"
            defaultValue={OrgRole.VIEWER}
            {...register('role')}
          >
            {assignableRoles.map((role) => (
              <SelectItem key={role} value={role} text={ROLE_LABEL[role]} />
            ))}
          </Select>
          <Button type="submit" size="md" disabled={isSubmitting}>
            Inviter
          </Button>
        </div>
      </Form>

      {link && (
        <div className="ddp-members__link">
          <InlineNotification
            kind="info"
            title="Lien d’invitation à usage unique"
            subtitle={`Transmettez ce lien à ${link.email}. Il n’est affiché qu’une fois et expire dans 7 jours.`}
            lowContrast
            hideCloseButton
          />
          <div className="ddp-members__link-row">
            <TextInput
              id="invite-link"
              labelText=""
              hideLabel
              readOnly
              value={link.url}
              onFocus={(e) => e.target.select()}
            />
            <Button kind="tertiary" size="md" renderIcon={Copy} onClick={() => void copyLink()}>
              {copied ? 'Copié' : 'Copier'}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

function formatDate(value: Date): string {
  return new Date(value).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
